import { KeychainAccess, SecureStorage } from "@aparajita/capacitor-secure-storage";
import { Capacitor } from "@capacitor/core";

import {
  getAccessToken,
  observability,
  setNativeDeviceTokenPersister,
  setNativeRefreshTokenPersister,
} from "../api";

// ipad-native-shell R2.4 / task 2.3: the native refresh token lives in the iOS
// Keychain, never `@capacitor/preferences` (UserDefaults is unencrypted and is
// copied into backups). `whenUnlockedThisDeviceOnly` + no iCloud sync keeps the
// grant on this one device: a restored backup or a second iPad signs in fresh.
const REFRESH_TOKEN_KEY = "native_refresh_token";
// Task 2.10: the device-trust token, same access settings. A separate item on
// purpose: sign-out clears the refresh token, never the device's trust.
const DEVICE_TOKEN_KEY = "native_device_token";

async function readItem(key: string): Promise<string> {
  try {
    return (await SecureStorage.getItem(key)) ?? "";
  } catch {
    return ""; // unreadable or corrupt item: behave as if never stored
  }
}

/** The access token's `sub`, which `observability.emit` needs before it sends;
 *  the server attributes the row from the Bearer token, not from this. */
function tokenSubject(): string | undefined {
  try {
    const payload = getAccessToken().split(".")[1];
    if (!payload) return undefined;
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { sub?: unknown };
    return typeof claims.sub === "string" ? claims.sub : undefined;
  } catch {
    return undefined;
  }
}

// One report per item per launch: a Keychain that refuses once refuses every
// rotation, and the first OSStatus is all the triage needs.
const reportedWriteFailures = new Set<string>();

/** A failed write is silent to the child (they sign in again next launch), so
 *  without this a signing or entitlement regression (-34018 on an unsigned
 *  build) is invisible in prod. Carries the OSStatus, never the value. */
function reportWriteFailure(key: string, op: "set" | "remove", err: unknown): void {
  if (reportedWriteFailures.has(key)) return;
  reportedWriteFailures.add(key);
  const e = err as { code?: unknown; message?: unknown } | null;
  const status = typeof e?.message === "string" ? /\((-?\d+)\)/.exec(e.message) : null;
  observability.emit({
    event_type: "native.keychain_write_failed",
    user_id: tokenSubject(),
    event_data: {
      item: key,
      op,
      code: typeof e?.code === "string" ? e.code : null,
      os_status: status ? Number(status[1]) : null,
    },
  });
}

/** Empty string deletes. Bridge calls are delivered in order, so back-to-back
 *  writes land last-write-wins. Resolves when the write lands (never rejects),
 *  so sign-out can await the refresh token's removal. */
function writeItem(key: string, value: string): Promise<void> {
  const write = value ? SecureStorage.setItem(key, value) : SecureStorage.removeItem(key);
  // The in-memory value still works for this launch.
  return write.then(
    () => {},
    (err: unknown) => reportWriteFailure(key, value ? "set" : "remove", err),
  );
}

/**
 * Load the stored refresh and device-trust tokens and install the Keychain
 * persisters behind `api.ts`'s seams. Await it before the first render so the boot-time refresh
 * sees the token. No-op off-native, and never throws: a Keychain failure means
 * the child signs in again, which is the same outcome as having no token.
 */
export async function installNativeAuthStorage(
  isNative: boolean = Capacitor.isNativePlatform(),
): Promise<void> {
  if (!isNative) return;

  try {
    await SecureStorage.setSynchronize(false);
    await SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly);
  } catch { /* iOS-only settings; Android keeps its Keystore defaults */ }

  const [refresh, device] = await Promise.all([
    readItem(REFRESH_TOKEN_KEY),
    readItem(DEVICE_TOKEN_KEY),
  ]);
  setNativeRefreshTokenPersister((token) => writeItem(REFRESH_TOKEN_KEY, token), refresh);
  setNativeDeviceTokenPersister((token) => writeItem(DEVICE_TOKEN_KEY, token), device);
}
